/**
 * @file Maintenance module Settings surface.
 */

import { appData, tempConfigData } from "../../app/state/state.js";
import { bindNativeFieldBehavior } from "../../shared/forms/nativeFieldStyles.js";
import { createSelectControl } from "../../shared/ui/controls/selectControl.js";
import { createUserOptions } from "../../shared/ui/controls/controlOptions.js";
import { openConfirmationModal } from "../../shared/ui/confirmationModal.js";
import { escapeHTML } from "../../shared/utils/contentUtils.js";
import {
    bindStatusPrioritySettingsControls,
    renderCategoryEditor,
    renderOptionEditor,
} from "./statusPrioritySettings.js";
import {
    bindTypeSettingsControls,
    closeTypeSettingsDropdownsOnOutsideClick,
    renderTypeEditor,
} from "./typeSettings.js";

const MAINTENANCE_STREAMS = Object.freeze([
    { value: "service", label: "Service" },
    { value: "issue", label: "Issue" },
]);
const MAINTENANCE_SETTINGS_LAYOUT_CLASSES = "maintenance-settings-layout w-full max-w-[92rem] space-y-8";
const MAINTENANCE_SETTINGS_CARD_CLASSES = "maintenance-settings-card bg-white p-6 rounded-lg shadow-md";

let showArchivedTechnicians = false;

function attrValue(value = "") {
    return escapeHTML(String(value ?? ""));
}

function getMaintenanceConfig() {
    if (!tempConfigData.maintenance ||
        typeof tempConfigData.maintenance !== "object") {
        tempConfigData.maintenance = {};
    }
    if (!Array.isArray(tempConfigData.maintenance.technicians)) {
        tempConfigData.maintenance.technicians = [];
    }
    if (!tempConfigData.maintenance.closeInterceptStatusByStream ||
        typeof tempConfigData.maintenance.closeInterceptStatusByStream !==
            "object") {
        tempConfigData.maintenance.closeInterceptStatusByStream = {
            service: null,
            issue: null,
        };
    }
    return tempConfigData.maintenance;
}

function renderOrderButton({index, direction, disabled}) {
    const icon = direction === "up" ? "expand_less" : "expand_more";
    const label = direction === "up" ? "Move technician up" :
        "Move technician down";
    const disabledAttr = disabled ? " disabled" : "";
    return `
        <button type="button" class="move-maintenance-technician-btn inline-flex h-7 w-7 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-gray-50 hover:text-[var(--dfc-brand-primary)] focus:outline-none focus-visible:text-[var(--dfc-brand-primary)] disabled:cursor-not-allowed disabled:opacity-25 disabled:hover:bg-transparent disabled:hover:text-gray-400" data-index="${index}" data-direction="${direction}" title="${label}" aria-label="${label}"${disabledAttr}>
            <span class="material-symbols-outlined pointer-events-none text-[18px]">${icon}</span>
        </button>
    `;
}

function getSortedTechnicianEntries(technicians) {
    return technicians
        .map((technician, index) => ({
            index,
            technician,
            order: Number(technician?.order ?? index + 1) || index + 1,
        }))
        .filter((entry) =>
            showArchivedTechnicians || entry.technician?.isActive !== false)
        .sort((a, b) => {
            const activeDiff = (a.technician?.isActive === false ? 1 : 0) -
                (b.technician?.isActive === false ? 1 : 0);
            if (activeDiff !== 0) {return activeDiff;}
            const orderDiff = a.order - b.order;
            return orderDiff !== 0 ? orderDiff : a.index - b.index;
        });
}

function getMaxActiveTechnicianOrder(technicians) {
    return Math.max(
        0,
        ...technicians
            .filter((technician) => technician?.isActive !== false)
            .map((technician) => Number(technician.order) || 0),
    );
}

function moveTechnician(index, direction) {
    const technicians = getMaintenanceConfig().technicians;
    const target = technicians[index];
    if (!target || typeof target !== "object") {return false;}

    const targetIsActive = target.isActive !== false;
    const entries = technicians
        .map((technician, technicianIndex) => ({
            index: technicianIndex,
            technician,
            order: Number(technician?.order ?? technicianIndex + 1) ||
                technicianIndex + 1,
        }))
        .filter((entry) =>
            (entry.technician?.isActive !== false) === targetIsActive)
        .sort((a, b) => {
            const orderDiff = a.order - b.order;
            return orderDiff !== 0 ? orderDiff : a.index - b.index;
        });
    const currentPosition = entries.findIndex((entry) => entry.index === index);
    const nextPosition = direction === "up"
        ? currentPosition - 1
        : currentPosition + 1;

    if (
        currentPosition < 0 ||
        nextPosition < 0 ||
        nextPosition >= entries.length
    ) {
        return false;
    }

    const current = entries[currentPosition].technician;
    const next = entries[nextPosition].technician;
    const currentOrder = Number(current.order) || entries[currentPosition].order;
    const nextOrder = Number(next.order) || entries[nextPosition].order;
    current.order = nextOrder;
    next.order = currentOrder;
    return true;
}

function userCanBeDefaultManager(user, selectedValue) {
    if (!user) {return false;}
    const userId = user.id || user.uid;
    if (userId && String(userId) === String(selectedValue)) {return true;}
    if (user.isActive === false) {return false;}
    if (user.role === "admin") {return true;}
    return Array.isArray(user.permissions) &&
        user.permissions.includes("maintenance");
}

function statusSupportsStream(status, stream) {
    if (!stream) {return true;}
    if (!Array.isArray(status?.streams) || status.streams.length === 0) {
        return true;
    }
    return status.streams.map(String).includes(stream);
}

function getClosedStatusOptions(stream, selectedValue) {
    const statuses = Array.isArray(tempConfigData.maintenanceStatus)
        ? [...tempConfigData.maintenanceStatus]
        : [];
    const options = statuses
        .sort((a, b) => (a.order || 0) - (b.order || 0))
        .filter((status) => {
            const isSelected = selectedValue &&
                String(status.id) === String(selectedValue);
            return isSelected ||
                (status.isClosed === true && statusSupportsStream(status, stream));
        })
        .map((status) => {
            const isEligible = status.isClosed === true &&
                statusSupportsStream(status, stream);
            return {
                value: status.id,
                label: status.name || status.id,
                accentColor: status.color,
                searchText: `${status.name || ""} ${status.id || ""}`,
                selectedOnly: !isEligible,
                unavailableLabel: !isEligible ? "saved" : "",
            };
        });

    if (
        selectedValue &&
        !options.some((option) => String(option.value) === String(selectedValue))
    ) {
        options.push({
            value: selectedValue,
            label: `Saved status (${selectedValue})`,
            selectedOnly: true,
            unavailableLabel: "missing",
        });
    }

    return options;
}

function renderTechnicianEditor() {
    const technicians = getMaintenanceConfig().technicians;
    const entries = getSortedTechnicianEntries(technicians);
    return `
        <div>
            <div class="settings-type-toolbar">
                <label class="block font-medium">Technicians</label>
                <label class="inline-flex items-center gap-2 text-xs text-gray-600">
                    <input type="checkbox" data-maintenance-tech-show-archived ${showArchivedTechnicians ? "checked" : ""}>
                    <span>Show archived</span>
                </label>
            </div>
            <div class="maintenance-technician-list">
                ${entries.map((entry) => {
        const isActive = entry.technician?.isActive !== false;
        const sameStateEntries = entries.filter((item) =>
            (item.technician?.isActive !== false) === isActive);
        const stateIndex = sameStateEntries.findIndex((item) =>
            item.index === entry.index);
        const isFirst = stateIndex === 0;
        const isLast = stateIndex === sameStateEntries.length - 1;
        const disabledAttr = isActive ? "" : " disabled";
        const hasBeenUsed = entry.technician?.hasBeenUsed === true;
        return `
            <div class="maintenance-technician-row ${isActive ? "" : "opacity-75"}">
                <div class="settings-config-order-controls maintenance-technician-order" aria-label="Technician order controls">
                    ${renderOrderButton({
                        index: entry.index,
                        direction: "up",
                        disabled: isFirst,
                    })}
                    ${renderOrderButton({
                        index: entry.index,
                        direction: "down",
                        disabled: isLast,
                    })}
                </div>
                <input type="text" value="${attrValue(entry.technician?.name || "")}" data-maintenance-tech-index="${entry.index}" class="settings-config-name maintenance-technician-name" placeholder="Technician name"${disabledAttr}>
                <span class="maintenance-technician-usage">${hasBeenUsed ? "Used" : "Unused"}</span>
                <div class="maintenance-technician-action">
                    <button type="button" class="archive-maintenance-technician-btn settings-config-archive ${isActive ? "" : "settings-config-restore"}" data-index="${entry.index}" title="${isActive ? "Archive technician" : "Restore technician"}" aria-label="${isActive ? "Archive technician" : "Restore technician"}">
                        <span class="material-symbols-outlined text-lg">${isActive ? "archive" : "unarchive"}</span>
                    </button>
                </div>
                <div class="maintenance-technician-action">
                    <button type="button" class="delete-maintenance-technician-btn settings-config-delete" data-index="${entry.index}" title="Delete technician" aria-label="Delete technician">
                        <span class="material-symbols-outlined text-lg">delete</span>
                    </button>
                </div>
            </div>
        `;
    }).join("")}
            </div>
            <button type="button" class="add-maintenance-technician-btn settings-add-row-btn mt-3">+ Add Technician</button>
        </div>
    `;
}

function renderWorkflowSettings() {
    const config = getMaintenanceConfig();
    return `
        <div class="grid gap-4 md:grid-cols-2">
            <div>
                <label class="block font-medium mb-2">Default Managed By</label>
                <div data-maintenance-default-manager-control></div>
                <p class="settings-helper-text mt-2">Used for new Maintenance items when active. Blank falls back to the current user.</p>
            </div>
            <div class="md:col-span-2">
                <h5 class="text-sm font-semibold text-gray-900">Maintenance completion statuses</h5>
                <p class="settings-helper-text mt-1">These closed statuses trigger the completion modal so technician, completed date, cost, and required repair details are captured before save.</p>
            </div>
            ${MAINTENANCE_STREAMS.map((stream) => `
                <div>
                    <label class="block font-medium mb-2">${stream.label} Close Status</label>
                    <div data-maintenance-close-status-control="${stream.value}"></div>
                </div>
            `).join("")}
            <div class="md:col-span-2">
                <label class="block font-medium mb-2">Service Recalculation Status</label>
                <div data-maintenance-recalculation-status-control></div>
                <p class="settings-helper-text mt-2">When a linked Service reaches this status, the Asset service dates recalculate from the Service completed date. Clearing the status disables automatic Service date recalculation.</p>
            </div>
        </div>
        <input type="hidden" value="${attrValue(config.defaultManagedBy || "")}" data-maintenance-default-manager-value>
    `;
}

/**
 * Renders the Maintenance Settings tab.
 * @param {HTMLElement} container Settings tab content container.
 * @param {{ updateSaveButton?: Function }} [options={}] Render dependencies.
 * @returns {void}
 */
export function renderMaintenanceSettingsTab(container, options = {}) {
    const { updateSaveButton = () => {} } = options;
    getMaintenanceConfig();
    if (!Array.isArray(tempConfigData.maintenanceStatus)) {
        tempConfigData.maintenanceStatus = [];
    }
    if (!Array.isArray(tempConfigData.maintenanceCategoryConfig)) {
        tempConfigData.maintenanceCategoryConfig = [];
    }
    if (!Array.isArray(tempConfigData.maintenanceTypes)) {
        tempConfigData.maintenanceTypes = [];
    }

    container.innerHTML = `
        <div class="${MAINTENANCE_SETTINGS_LAYOUT_CLASSES}">
            <div class="${MAINTENANCE_SETTINGS_CARD_CLASSES}">
                <h4 class="text-lg font-semibold mb-4 border-b pb-2">Maintenance Status Groups</h4>
                ${renderCategoryEditor("Status Groups", "maintenanceCategoryConfig", tempConfigData.maintenanceCategoryConfig)}
            </div>
            <div class="${MAINTENANCE_SETTINGS_CARD_CLASSES}">
                <h4 class="text-lg font-semibold mb-4 border-b pb-2">Maintenance Statuses</h4>
                ${renderOptionEditor("Status Options", "maintenanceStatus", tempConfigData.maintenanceStatus, true, tempConfigData.maintenanceCategoryConfig)}
            </div>
            <div class="${MAINTENANCE_SETTINGS_CARD_CLASSES}">
                <h4 class="text-lg font-semibold mb-4 border-b pb-2">Maintenance Types</h4>
                ${renderTypeEditor("Types", "maintenanceTypes", tempConfigData.maintenanceTypes)}
            </div>
            <div class="${MAINTENANCE_SETTINGS_CARD_CLASSES}">
                <h4 class="text-lg font-semibold mb-4 border-b pb-2">Technicians</h4>
                ${renderTechnicianEditor()}
            </div>
            <div class="${MAINTENANCE_SETTINGS_CARD_CLASSES}">
                <h4 class="text-lg font-semibold mb-4 border-b pb-2">Defaults And Close Flow</h4>
                ${renderWorkflowSettings()}
            </div>
        </div>
    `;

    bindStatusPrioritySettingsControls(container);
    bindTypeSettingsControls(container);
    bindMaintenanceSettingsControls(container, { updateSaveButton });

    document.removeEventListener("click", closeTypeSettingsDropdownsOnOutsideClick);
    document.addEventListener("click", closeTypeSettingsDropdownsOnOutsideClick);
}

function bindMaintenanceSettingsControls(container, { updateSaveButton }) {
    bindNativeFieldBehavior(container);
    const config = getMaintenanceConfig();
    const selectedDefault = config.defaultManagedBy || "";
    const managerUsers = (appData.users || [])
        .filter((user) => userCanBeDefaultManager(user, selectedDefault))
        .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    const managerOptions = createUserOptions(managerUsers, {
        selectedValues: selectedDefault ? [selectedDefault] : [],
    });

    const managerRoot = container.querySelector(
        "[data-maintenance-default-manager-control]",
    );
    if (managerRoot) {
        createSelectControl({
            root: managerRoot,
            accessibleName: "Default Maintenance manager",
            value: selectedDefault,
            options: managerOptions,
            clearable: true,
            searchable: managerOptions.length >= 7,
            searchThreshold: 7,
            portalMenu: true,
            placeholder: "Current user fallback",
            mirror: false,
            onChange: (value) => {
                config.defaultManagedBy = value || null;
                updateSaveButton(true);
            },
        });
    }

    MAINTENANCE_STREAMS.forEach((stream) => {
        const root = container.querySelector(
            `[data-maintenance-close-status-control="${stream.value}"]`,
        );
        if (!root) {return;}
        const selected = config.closeInterceptStatusByStream?.[stream.value] || "";
        createSelectControl({
            root,
            accessibleName: `${stream.label} close status`,
            value: selected,
            options: getClosedStatusOptions(stream.value, selected),
            clearable: true,
            portalMenu: true,
            placeholder: "Select closed status",
            mirror: false,
            onChange: (value) => {
                config.closeInterceptStatusByStream[stream.value] = value || null;
                updateSaveButton(true);
            },
        });
    });

    const recalculationRoot = container.querySelector(
        "[data-maintenance-recalculation-status-control]",
    );
    if (recalculationRoot) {
        const selected = config.serviceRecalculationStatusId || "";
        createSelectControl({
            root: recalculationRoot,
            accessibleName: "Service recalculation status",
            value: selected,
            options: getClosedStatusOptions("service", selected),
            clearable: true,
            portalMenu: true,
            placeholder: "Select Service closed status",
            mirror: false,
            onChange: (value) => {
                config.serviceRecalculationStatusId = value || null;
                updateSaveButton(true);
            },
        });
    }
}

/**
 * Handles Maintenance Settings input changes.
 * @param {HTMLElement} target Event target.
 * @param {{ updateSaveButton?: Function, rerenderCurrentTabContent?: Function }} [options={}] Handler dependencies.
 * @returns {boolean} True when handled.
 */
export function handleMaintenanceSettingsChange(target, options = {}) {
    const {
        rerenderCurrentTabContent = () => {},
        updateSaveButton = () => {},
    } = options;

    if (target.matches("[data-maintenance-tech-show-archived]")) {
        showArchivedTechnicians = target.checked;
        rerenderCurrentTabContent();
        return true;
    }

    if (target.matches("[data-maintenance-tech-index]")) {
        const index = parseInt(target.dataset.maintenanceTechIndex, 10);
        const technician = getMaintenanceConfig().technicians[index];
        if (!technician || technician.isActive === false) {return true;}
        technician.name = target.value;
        updateSaveButton(true);
        return true;
    }

    return false;
}

/**
 * Handles Maintenance Settings button actions.
 * @param {HTMLElement} target Event target.
 * @param {{ updateSaveButton?: Function, rerenderCurrentTabContent?: Function }} [options={}] Handler dependencies.
 * @returns {boolean} True when handled.
 */
export function handleMaintenanceSettingsButtonClick(target, options = {}) {
    const {
        rerenderCurrentTabContent = () => {},
        updateSaveButton = () => {},
    } = options;
    const config = getMaintenanceConfig();

    if (target.closest(".add-maintenance-technician-btn")) {
        config.technicians.push({
            id: `tech_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`,
            name: "New Technician",
            isActive: true,
            order: getMaxActiveTechnicianOrder(config.technicians) + 1,
            hasBeenUsed: false,
        });
        updateSaveButton(true);
        rerenderCurrentTabContent();
        return true;
    }

    if (target.closest(".move-maintenance-technician-btn")) {
        const btn = target.closest(".move-maintenance-technician-btn");
        const index = parseInt(btn.dataset.index, 10);
        if (moveTechnician(index, btn.dataset.direction)) {
            updateSaveButton(true);
            rerenderCurrentTabContent();
        }
        return true;
    }

    if (target.closest(".archive-maintenance-technician-btn")) {
        const btn = target.closest(".archive-maintenance-technician-btn");
        const index = parseInt(btn.dataset.index, 10);
        const technician = config.technicians[index];
        if (!technician || typeof technician !== "object") {return true;}

        if (technician.isActive === false) {
            technician.isActive = true;
            technician.order = getMaxActiveTechnicianOrder(config.technicians) + 1;
        } else {
            technician.isActive = false;
        }
        updateSaveButton(true);
        rerenderCurrentTabContent();
        return true;
    }

    if (target.closest(".delete-maintenance-technician-btn")) {
        const btn = target.closest(".delete-maintenance-technician-btn");
        const index = parseInt(btn.dataset.index, 10);
        const technician = config.technicians[index];
        if (!technician || typeof technician !== "object") {return true;}

        if (technician.hasBeenUsed === true) {
            openConfirmationModal(
                "Delete Used Technician?",
                "This technician has been used on Maintenance items. If you delete it, older items will keep their saved technician text, but they will no longer be grouped under this configured technician option. Archiving is recommended unless you are sure the option should be removed.",
                () => {
                    config.technicians.splice(index, 1);
                    updateSaveButton(true);
                    rerenderCurrentTabContent();
                },
                true,
                "Delete",
            );
            return true;
        }

        openConfirmationModal(
            "Delete Technician?",
            "This technician has not been used by any Maintenance item. Delete it from the settings list?",
            () => {
                config.technicians.splice(index, 1);
                updateSaveButton(true);
                rerenderCurrentTabContent();
            },
        );
        return true;
    }

    return false;
}
